"use client";

import { AnimatePresence, motion } from "framer-motion";
import { type LucideIcon, Mail, Phone, Timer } from "lucide-react";
import { useRef } from "react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { BRAND_LOGO, EASE } from "../../constants";
import { useStickToBottom } from "../../use-stick-to-bottom";
import { MAYA, type Offer, type ThreadItem } from "./data";

export type Status = "active" | "atrisk" | "reengaged" | "unlimited";

const STATUS_LABEL: Record<Status, string> = {
  active: "Active member",
  atrisk: "Churn risk",
  reengaged: "Re-engaged",
  unlimited: "Unlimited member",
};

const STATUS_CLASS: Record<Status, string> = {
  active: "text-muted-foreground",
  atrisk: "text-amber-600 dark:text-amber-400",
  reengaged: "text-success",
  unlimited: "text-primary dark:text-[hsl(var(--brand-accent))]",
};

function Bubble({
  direction,
  children,
}: {
  direction: "incoming" | "outgoing";
  children: React.ReactNode;
}) {
  const out = direction === "outgoing";
  return (
    <div className={cn("w-full", out ? "text-right" : "text-left")}>
      <div
        className={cn(
          "inline-block max-w-[15rem] whitespace-pre-line rounded-2xl border px-3 py-2 text-left text-[13px] leading-snug sm:max-w-[17rem]",
          out
            ? "rounded-br-md border-transparent bg-outgoing-bubble text-white"
            : "rounded-bl-md border-border bg-incoming-bubble text-foreground",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function OfferCard({ offer }: { offer: Offer }) {
  return (
    <div className="mt-1.5 flex justify-end">
      <div className="w-[15rem] rounded-xl border border-border bg-background p-3 text-left shadow-sm sm:w-[17rem]">
        <div className="flex items-center gap-1.5">
          <img
            src={BRAND_LOGO}
            alt={offer.brand}
            className="h-4 w-4 shrink-0 rounded-[4px] object-cover"
          />
          <span className="text-[10px] font-bold uppercase tracking-wide text-foreground">
            {offer.brand}
          </span>
        </div>
        <p className="mt-1.5 text-sm font-semibold text-foreground">{offer.title}</p>
        <p className="text-xs text-muted-foreground">{offer.detail}</p>
        <span className="mt-2 inline-block rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground">
          {offer.cta}
        </span>
      </div>
    </div>
  );
}

function EventChip({ icon: Icon, title, sub }: { icon: LucideIcon; title: string; sub?: string }) {
  return (
    <div className="flex justify-center">
      <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-x-1.5 rounded-2xl border border-border bg-background px-2.5 py-1 text-center text-[11px] shadow-sm">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-foreground">
          <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
          {title}
        </span>
        {sub ? <span className="whitespace-nowrap text-muted-foreground">· {sub}</span> : null}
      </span>
    </div>
  );
}

function SystemLine({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
      <Timer className="h-3 w-3" />
      <span>{text}</span>
    </div>
  );
}

function Row({ item }: { item: ThreadItem }) {
  switch (item.kind) {
    case "event":
      return <EventChip icon={item.icon} title={item.title} sub={item.sub} />;
    case "system":
      return <SystemLine text={item.text} />;
    case "message":
      return (
        <div>
          <Bubble direction={item.direction}>{item.body}</Bubble>
          {item.offer ? <OfferCard offer={item.offer} /> : null}
        </div>
      );
    default:
      return null;
  }
}

export function Thread({
  items,
  visible,
  status,
  reduce,
}: {
  items: ThreadItem[];
  visible: number;
  status: Status;
  reduce: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the latest revealed item in view by scrolling ONLY this container —
  // never scrollIntoView, which would also yank the whole page. Backs off if the
  // user scrolls up, resumes when they return to the bottom.
  useStickToBottom(scrollRef, visible, reduce);

  const shown = items.slice(0, visible);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Avatar name={MAYA.name} src={MAYA.src} size="xs" />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium leading-tight text-foreground">
            {MAYA.name}
          </p>
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={status}
              initial={reduce ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -4 }}
              transition={{ duration: 0.25, ease: EASE }}
              className={cn("text-[11px] font-medium leading-tight", STATUS_CLASS[status])}
            >
              {STATUS_LABEL[status]}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted/60 sm:px-2.5 sm:py-1"
          >
            <Mail className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Send email</span>
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted/60 sm:px-2.5 sm:py-1"
          >
            <Phone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Call</span>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-3 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {shown.map((item) => (
          <motion.div
            key={item.id}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <Row item={item} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
